import React, { useEffect, useMemo, useState } from 'react';

import CalculatorDisplay from './calculator-display/CalculatorDisplay';
import ConvertRateDetailsModal from './convert-rate-details-modal/ConvertRateDetailsModal';
import fallbackDictionary from './fallbackDictionary';
import { changeCurrency, getInitialCalculatorData, getInitialRates, swap } from './functions/calculator-functions';
import SelectCurrencyModal from './select-currency-modal/SelectCurrencyModal';
import {
    Calculator,
    CalculatorFieldName,
    CurrencyDetails,
    ExchangeRateResult,
    FetchError,
    Piwik,
    SiteCoreDictionary,
} from './types';
import VisaFxSimpleCalculatorDesktopSkeleton from './visa-exchange-calculator-skeleton/VisaFxSimpleCalculatorDesktopSkeleton';
import VisaFxSimpleCalculatorMobileSkeleton from './visa-exchange-calculator-skeleton/VisaFxSimpleCalculatorMobileSkeleton';

interface VisaFxSimpleCalculatorProps {
    /** Promise function to fetch available currencies the user can select */
    getAvailableCurrencies: () => Promise<CurrencyDetails[]>;
    /** Promise function to fetch detailed markup in more information modal */
    getDetailedMarkup: (amount: number, fromCurrency: CurrencyDetails, toCurrency: CurrencyDetails) => void;
    /** Promise function to fetch the rate between two currency codes */
    getRateBothWays: (currencyCode1: string, currencyCode2: string, rateAt?: string) => Promise<ExchangeRateResult[]>;
    /** Link to the more detailed price list */
    priceListPath: string;
    /** Whether you want the number input on the left and the currency input on the right */
    currencyOnRightSide?: boolean;
    /** Sitecore dictionary */
    sitecoreDictionary?: SiteCoreDictionary;
    /** PIWIK tags */
    piwik?: Piwik;
}

const VisaFxSimpleCalculator = ({
    getAvailableCurrencies,
    getDetailedMarkup,
    getRateBothWays,
    priceListPath,
    currencyOnRightSide = false,
    sitecoreDictionary,
    piwik,
}: VisaFxSimpleCalculatorProps) => {
    const [availableCurrencies, setAvailableCurrencies] = useState<CurrencyDetails[] | undefined>(undefined);
    const [calculator, setCalculator] = useState<Calculator | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [fetchedRates, setFetchedRates] = useState<ExchangeRateResult[]>([]);
    const [focusedField, setFocusedField] = useState<CalculatorFieldName | undefined>(undefined);
    const [loadingInitialData, setLoadingInitialData] = useState<boolean>(false);
    const [loadingRate, setLoadingRate] = useState<boolean>(false);
    const [rateError, setRateError] = useState<string | undefined>(undefined);
    const [showDetailedInfoModal, setShowDetailedInfoModal] = useState<boolean>(false);
    const [showSelectCurrencyModalWithField, setShowSelectCurrencyModalWithField] = useState<
        CalculatorFieldName | undefined
    >(undefined);
    const [width, setWidth] = useState<number>(window.innerWidth);
    const dictionary: SiteCoreDictionary = { ...fallbackDictionary, ...sitecoreDictionary };

    useEffect(() => {
        setLoadingInitialData(true);
        getAvailableCurrencies()
            .then((availableCurrenciesResult) => {
                setAvailableCurrencies(availableCurrenciesResult);
                getInitialRates(getRateBothWays)
                    .then((initialRatesResult) => {
                        const initialRates = initialRatesResult.flat();
                        setLoadingInitialData(false);
                        setFetchedRates(initialRates);
                        setCalculator(getInitialCalculatorData(availableCurrenciesResult, initialRates));
                    })
                    .catch(({ errorName }: FetchError) => {
                        setError(errorName);
                    });
            })
            .catch(({ errorName }: FetchError) => {
                setError(errorName);
            });
    }, [getAvailableCurrencies, getRateBothWays]);

    useEffect(() => {
        getAvailableCurrencies();
    }, [getAvailableCurrencies]);

    useEffect(() => {
        const handleWindowSizeChange = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleWindowSizeChange);

        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    const onClickMoreInfo = () => {
        if (typeof calculator === 'undefined') {
            return;
        }

        const { from, to } = calculator;

        if (typeof from.amount === 'undefined') {
            return;
        }

        // We want to get this data for demo purposes, but we do not display it to user yet
        getDetailedMarkup(from.amount, from.currency, to.currency);
        setShowDetailedInfoModal(true);
    };

    const onClickSelectCurrency = (field: CalculatorFieldName) => {
        setShowSelectCurrencyModalWithField(field);
    };

    const selectedCurrencyCodes: string[] = useMemo(() => {
        if (typeof calculator === 'undefined') {
            return [];
        }

        return [calculator.from.currency.currencyCode, calculator.to.currency.currencyCode];
    }, [calculator]);

    const isMobile = width <= 768;

    if (loadingInitialData) {
        return isMobile ? <VisaFxSimpleCalculatorMobileSkeleton /> : <VisaFxSimpleCalculatorDesktopSkeleton />;
    }

    if (
        typeof error !== 'undefined' ||
        typeof calculator === 'undefined' ||
        typeof availableCurrencies === 'undefined'
    ) {
        return null;
    }

    const onChangeCurrency = (currency: CurrencyDetails | undefined, field: CalculatorFieldName) => {
        setShowSelectCurrencyModalWithField(undefined);

        changeCurrency(
            calculator,
            fetchedRates,
            field,
            currency,
            setCalculator,
            setFetchedRates,
            setFocusedField,
            setLoadingRate,
            setRateError,
            getRateBothWays,
        );
    };

    const swapCurrencies = () => {
        swap(calculator, setCalculator, setFocusedField);
    };

    return (
        <>
            <CalculatorDisplay
                availableCurrencies={availableCurrencies}
                calculator={calculator}
                focusedField={focusedField}
                isMobile={isMobile}
                loadingRate={loadingRate}
                onChangeCurrency={onChangeCurrency}
                onClickMoreInfo={onClickMoreInfo}
                onClickSelectCurrency={onClickSelectCurrency}
                rateError={rateError}
                setCalculator={setCalculator}
                setFocusedField={setFocusedField}
                swapCurrencies={swapCurrencies}
                currencyOnRightSide={currencyOnRightSide}
                dictionary={dictionary}
                piwik={piwik}
            />
            <ConvertRateDetailsModal
                calculator={calculator}
                setShowDetailedInfoModal={setShowDetailedInfoModal}
                showDetailedInfoModal={showDetailedInfoModal}
                priceListPath={priceListPath}
                dictionary={dictionary}
            />
            <SelectCurrencyModal
                availableCurrencies={availableCurrencies}
                onChangeCurrency={onChangeCurrency}
                setShowSelectCurrencyModalWithField={setShowSelectCurrencyModalWithField}
                showSelectCurrencyModalWithField={showSelectCurrencyModalWithField}
                selectedCurrencyCodes={selectedCurrencyCodes}
                dictionary={dictionary}
                piwik={piwik}
            />
        </>
    );
};

export default VisaFxSimpleCalculator;
